import React, { useEffect, useMemo, useState } from 'react';

import { Check, Plus, Search } from 'lucide-react';

import useDebouncedValue from '../../hooks/useDebouncedValue';
import { useExploreCourses } from '../../hooks/useExploreCourses';
import { CourseMinifiedDTO } from '../../services/apiClient';
import { courseLogo } from '../../utils/courseClosure';
import { createCourseSearch } from '../../utils/courseSearch';
import ClosedBadge from '../common/ClosedBadge';
import Drawer from '../common/Drawer';
import Loader from '../common/Loader';

// "Search for a course and add it to my list", shared by the member's Home Courses and Courses
// Played pages.
//
// It searches the same course dataset the Explore map loads (useExploreCourses), in the browser,
// with the same search the map's search bar uses (utils/courseSearch.ts). That dataset is already published as a
// snapshot for the map, so picking a course costs no new search endpoint and no request per
// keystroke.
//
// The dataset is several MB, so it is only loaded once the drawer is first opened (see the
// wrapper at the bottom), and then kept for the life of the page so reopening is instant.

const SEARCH_MIN_CHARS = 2;
const MAX_RESULTS = 20;

interface CoursePickerDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  // The courses already on the member's list, lower-cased. Shown as added, and not offered again.
  selectedCourseIds: Set<string>;
  // Resolves once the course is saved. A rejection leaves the row offering "Add" again; the page
  // owns what the member is told about it (a toast, or the upgrade drawer).
  onAdd: (course: CourseMinifiedDTO) => Promise<void>;
}

const CoursePickerContent: React.FC<CoursePickerDrawerProps> = ({
  isOpen,
  onClose,
  title,
  selectedCourseIds,
  onAdd,
}) => {
  const { courses, isLoading, error } = useExploreCourses();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 250);
  const [pendingIds, setPendingIds] = useState<Set<string>>(() => new Set());

  // A fresh search each time the drawer opens, rather than last visit's results.
  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  const courseSearch = useMemo(() => createCourseSearch(courses), [courses]);

  const isSearching = debouncedSearchTerm.trim().length >= SEARCH_MIN_CHARS;

  const results = useMemo(() => {
    if (!isSearching) {
      return [];
    }
    return courseSearch(debouncedSearchTerm, MAX_RESULTS);
  }, [courseSearch, debouncedSearchTerm, isSearching]);

  const handleAdd = async (course: CourseMinifiedDTO): Promise<void> => {
    if (course.id == null) {
      return;
    }

    const key = course.id.toLowerCase();
    setPendingIds((current) => new Set(current).add(key));

    try {
      await onAdd(course);
    } catch {
      // Reported by the page. Nothing to do here but let the row offer "Add" again.
    } finally {
      setPendingIds((current) => {
        const next = new Set(current);
        next.delete(key);
        return next;
      });
    }
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title={title} size="small">
      <div className="flex flex-col gap-4">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
          <input
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={
              isLoading
                ? 'Loading courses...'
                : `Search ${courses.length.toLocaleString()} courses...`
            }
            disabled={isLoading}
            className="h-11 w-full rounded-md border border-gray-300 bg-white pl-9 pr-3 text-sm text-ink outline-none transition-colors placeholder:text-ink-muted focus:border-primary focus:ring-1 focus:ring-primary disabled:cursor-wait disabled:opacity-70 dark:border-white/20 dark:bg-white/5 dark:text-white"
          />
        </div>

        {isLoading && (
          <div className="flex justify-center py-8">
            <Loader />
          </div>
        )}

        {!isLoading && error != null && courses.length === 0 && (
          <p className="text-sm text-red-600">
            Could not load the course list. Close this and try again.
          </p>
        )}

        {!isLoading && !isSearching && courses.length > 0 && (
          <p className="text-sm text-ink-muted dark:text-white/60">
            Type at least {SEARCH_MIN_CHARS} letters of the course name.
          </p>
        )}

        {isSearching && results.length === 0 && !isLoading && (
          <p className="text-sm text-ink-muted dark:text-white/60">
            No courses match that.
          </p>
        )}

        {results.length > 0 && (
          <ul className="divide-y divide-gray-100 rounded-md border border-gray-200 dark:divide-white/10 dark:border-white/10">
            {results.map((course) => {
              const key = course.id?.toLowerCase() ?? '';
              const isAdded = selectedCourseIds.has(key);
              const isPending = pendingIds.has(key);

              return (
                <li
                  key={course.id}
                  className="flex items-center gap-3 px-3 py-2.5"
                >
                  {/* Same white plate and default mark as the map's search results, so a course
                      with no logo of its own still lines up with the rest. */}
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-white p-0.5 ring-1 ring-black/10">
                    <img
                      src={courseLogo(course).src}
                      alt=""
                      className="h-full w-full object-contain"
                      draggable={false}
                    />
                  </span>

                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm font-medium text-ink dark:text-white">
                      {course.name}
                    </span>
                    <span className="flex min-w-0 items-center gap-1.5">
                      <span className="truncate text-xs text-ink-muted dark:text-white/60">
                        {[course.stateName, course.countryName]
                          .filter(Boolean)
                          .join(', ')}
                      </span>
                      <ClosedBadge
                        tone="light"
                        closed={course.closed}
                        closedYear={course.closedYear}
                        closedMonth={course.closedMonth}
                      />
                    </span>
                  </span>

                  {isAdded ? (
                    <span className="inline-flex h-9 shrink-0 items-center gap-1 px-2 text-sm font-medium text-ink-muted dark:text-white/60">
                      <Check className="h-4 w-4" />
                      Added
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => void handleAdd(course)}
                      disabled={isPending}
                      aria-label={`Add ${course.name ?? 'this course'}`}
                      className="inline-flex h-9 shrink-0 cursor-pointer items-center gap-1 rounded-md bg-primary px-3 text-sm font-semibold text-secondary transition hover:bg-opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-60"
                    >
                      {isPending ? (
                        <Loader size="sm" />
                      ) : (
                        <Plus className="h-4 w-4" />
                      )}
                      Add
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Drawer>
  );
};

// Holds the course dataset back until the drawer is first opened. After that the content stays
// mounted (Drawer itself renders nothing while closed), so the dataset is not fetched again.
const CoursePickerDrawer: React.FC<CoursePickerDrawerProps> = (props) => {
  const [hasOpened, setHasOpened] = useState(props.isOpen);

  useEffect(() => {
    if (props.isOpen) {
      setHasOpened(true);
    }
  }, [props.isOpen]);

  return hasOpened ? <CoursePickerContent {...props} /> : null;
};

export default CoursePickerDrawer;
